/**
 * URL Validation Utilities
 *
 * Provides functions to validate and sanitize URLs for iframe embedding.
 * Prevents XSS attacks via javascript:, data:, and other dangerous protocols.
 *
 * @module minting/utils/urlValidator
 * @category Minting Framework
 */

/**
 * Validates a URL against allowed criteria.
 *
 * Checks:
 * - Protocol must be http: or https:
 * - If allowedOrigins provided, URL host must match one of them
 *
 * @param url - The URL to validate (string)
 * @param allowedOrigins - Optional array of allowed origins (e.g., ['https://example.com'])
 * @returns true if URL is valid, false otherwise
 *
 * @example
 * ```typescript
 * validateUrl('https://safe.com'); // true
 * validateUrl('javascript:alert(1)'); // false
 * validateUrl('https://trusted.com', ['https://trusted.com']); // true
 * validateUrl('https://evil.com', ['https://trusted.com']); // false
 * ```
 */
export function validateUrl(url: string, allowedOrigins?: string[]): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Only allow http/https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      console.warn('[urlValidator] Blocked URL with disallowed protocol:', urlObj.protocol);
      return false;
    }

    // Check allowed origins if provided
    if (allowedOrigins && Array.isArray(allowedOrigins) && allowedOrigins.length > 0) {
      // urlObj.host already contains the port when non-default; use origin
      // to avoid double-porting (e.g. http://127.0.0.1:4321:4321).
      const origin = urlObj.origin;
      const isAllowed = allowedOrigins.includes(origin);
      if (!isAllowed) {
        console.warn('[urlValidator] Origin not in allowed list:', origin, 'Allowed:', allowedOrigins);
      }
      return isAllowed;
    }

    return true;
  } catch (error) {
    // Invalid URL
    console.warn('[urlValidator] Invalid URL:', url, error);
    return false;
  }
}

/**
 * Sanitizes a URL by ensuring it's valid and removing dangerous protocols.
 *
 * @param url - The URL to sanitize
 * @returns The sanitized URL string, or null if URL is invalid or dangerous
 *
 * @example
 * ```typescript
 * sanitizeUrl('https://example.com'); // returns 'https://example.com'
 * sanitizeUrl('javascript:alert(1)'); // returns null
 * sanitizeUrl('data:text/html,<script>alert(1)</script>'); // returns null
 * ```
 */
export function sanitizeUrl(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const urlObj = new URL(url);

    // Block dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
    if (dangerousProtocols.includes(urlObj.protocol)) {
      console.warn('[urlValidator] Blocked dangerous URL protocol:', urlObj.protocol, 'URL:', url);
      return null;
    }

    // Only allow http/https
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      console.warn('[urlValidator] Blocked non-http(s) protocol:', urlObj.protocol);
      return null;
    }

    // Return normalized URL
    return urlObj.toString();
  } catch (error) {
    console.warn('[urlValidator] Failed to parse URL:', url, error);
    return null;
  }
}

/**
 * Extract the origin from a URL (protocol + host + optional port)
 *
 * @param url - The URL to extract origin from
 * @returns The origin string, or null if invalid URL
 */
export function extractOrigin(url: string): string | null {
  try {
    const urlObj = new URL(url);
    // urlObj.host already includes the port when non-default; origin is the
    // correct single-shot form (avoids the http://host:port:port bug).
    return urlObj.origin;
  } catch {
    return null;
  }
}

/**
 * Check if a URL is same-origin with the current page.
 *
 * @param url - The URL to check
 * @returns true if same origin, false otherwise
 */
export function isSameOrigin(url: string): boolean {
  const origin = extractOrigin(url);
  if (!origin) return false;
  return origin === window.location.origin;
}
